import {
  AssociationItem,
  EditorialPerInitiative,
} from "../../AddActivity/types";
import {
  CompletionCriterion,
  ComplexityLevel,
  CourseWizardData,
  EMPTY_COURSE_DATA,
  PropaedeuticLogic,
  SelectedActivity,
  TestLogic,
} from "../types";
import {
  mapLOChildrenById,
  mapLOStructureToSelectedItems,
} from "../../utils/loStructure";
import { isNoObsolescenceDate } from "../../utils/obsolescence";

const SENTINEL_FAR_FUTURE = 253402214400000;

const toIsoOrNull = (d: string | number | null | undefined): string | null => {
  if (d == null) return null;
  if (typeof d === "number") {
    if (d === SENTINEL_FAR_FUTURE) return null;
    return new Date(d).toISOString();
  }
  return d;
};

const toAssociationItem = (
  v: { id: number | string; label?: string; name?: string } | null | undefined,
): AssociationItem | null => {
  if (!v || v.id == null) return null;
  return { id: Number(v.id), label: v.label || v.name || `#${v.id}` };
};

const toAssociationList = (
  list:
    | Array<{ id: number | string; label?: string; name?: string }>
    | null
    | undefined,
): AssociationItem[] => {
  if (!list || !Array.isArray(list)) return [];
  return list
    .map((it) => toAssociationItem(it))
    .filter((x): x is AssociationItem => x !== null);
};

const numToString = (n: number | string | null | undefined): string => {
  if (n == null) return "";
  return String(n);
};

const detectCompletionCriterion = (
  detail: Record<string, any>,
): { criterion: CompletionCriterion | null; value: string } => {
  if (detail.completion_criteria_lo_required === true) {
    return { criterion: "LO_REQUIRED", value: "" };
  }

  const isMeaningful = (v: any): boolean => v != null && Number(v) > 0;

  if (isMeaningful(detail.lo_to_complete)) {
    return {
      criterion: "LO_TO_COMPLETE",
      value: numToString(detail.lo_to_complete),
    };
  }
  if (isMeaningful(detail.min_completition_perc)) {
    return {
      criterion: "MIN_COMPLETION_PERC",
      value: numToString(detail.min_completition_perc),
    };
  }
  if (isMeaningful(detail.completition_minutes)) {
    return {
      criterion: "COMPLETION_MINUTES",
      value: numToString(detail.completition_minutes),
    };
  }
  if (isMeaningful(detail.completition_minutes_perc)) {
    return {
      criterion: "COMPLETION_MINUTES_PERC",
      value: numToString(detail.completition_minutes_perc),
    };
  }
  return { criterion: null, value: "" };
};

const toSelectedActivity = (item: {
  id: number | string;
  title?: string;
  label?: string;
  name?: string;
  category?: string;
  type?: string;
  learning_object_type?: string;
  [k: string]: any;
}): SelectedActivity => {
  const id = Number(item.id);
  const title = item.title || item.label || item.name || `#${id}`;
  const type = item.category || item.learning_object_type || item.type || "";
  return {
    id,
    title,
    type,
    kind: "activity",
    entity: {
      ...item,
      id,
      title,
      type,
    },
    locked: true,
  };
};

export function mapCourseDetailToWizardData(
  detail: Record<string, any>,
  editorialByInitiative: Record<number, EditorialPerInitiative> = {},
  children: readonly Record<string, any>[] = [],
): CourseWizardData {
  const { criterion, value } = detectCompletionCriterion(detail);

  const publishDate = toIsoOrNull(detail.publish_date);
  const obsolescentDate = isNoObsolescenceDate(detail.obsolescent_date)
    ? null
    : toIsoOrNull(detail.obsolescent_date);

  const isStandalone = !!detail.is_standalone;
  const schedulePublication = isStandalone && !!publishDate;
  const childrenById = mapLOChildrenById(children);
  const selectedActivities = Array.isArray(detail.structure)
    ? mapLOStructureToSelectedItems(detail.structure, (id) =>
        toSelectedActivity({ ...childrenById.get(id), id }),
      )
    : Array.isArray(detail.didactical_activity)
      ? detail.didactical_activity.map(toSelectedActivity)
      : [];

  return {
    ...EMPTY_COURSE_DATA,

    // Step 1
    title: detail.title || "",
    digited_code: detail.digited_code || "",
    detail_description: detail.detail_description || "",
    allow_post_publish_additions: !detail.completable, // semantica inversa
    is_standalone: isStandalone,
    schedule_publication: schedulePublication,
    publish_date: publishDate,
    schedule_obsolescence: isStandalone && !!obsolescentDate,
    obsolescent_date: obsolescentDate,
    propaedeutic_logics: (detail.propaedeutic_logics ||
      null) as PropaedeuticLogic | null,
    completion_criterion: criterion,
    completion_value: value,
    cover_id: toAssociationItem(detail.cover_id),
    video_id: toAssociationItem(detail.video_id),

    // Step 2
    certificate_ids: toAssociationList(detail.certificate_ids),
    signature_certificate_ids: toAssociationList(
      detail.signature_certificate_ids,
    ),
    badge_id: toAssociationItem(detail.badge_id),
    complexity: (detail.complexity || null) as ComplexityLevel | null,
    score: numToString(detail.score),
    credits: numToString(detail.credits),
    lo_tag: Array.isArray(detail.lo_tag) ? detail.lo_tag : [],
    useful_links: Array.isArray(detail.useful_links) ? detail.useful_links : [],
    stakeholders: Array.isArray(detail.stakeholders) ? detail.stakeholders : [],
    time_slots: Array.isArray(detail.time_slots) ? detail.time_slots : [],
    didactical_material: toAssociationList(detail.didactical_material),

    // Step 3
    selected_activities: selectedActivities,

    // Step 4
    test_id: toAssociationItem(detail.test_id),
    survey_id: toAssociationItem(detail.survey_id),
    test_logic: (detail.test_logic || null) as TestLogic | null,
    test_min_score: numToString(detail.test_min_score),
    max_attempt: numToString(detail.max_attempt),

    // Step 5 — iniziative associate.
    // Il campo BE è `initiative`.
    selected_initiatives: toAssociationList(detail.initiative),

    // Step 6 — editoriali.
    editorial_per_initiative: editorialByInitiative,
    highlight_explore: !!detail.highlight_explore,
  };
}
